import type { NextFont } from 'next/dist/compiled/@next/font';

import {
  ResumeFontName,
  DeprecatedResumeFontName,
  ARIMO_FONT_NAME,
  CINZEL_FONT_NAME,
  CRIMSON_PRO_FONT_NAME,
  DEPRECATED_CINSEL_FONT_NAME,
  DEPRECATED_MULI_FONT_NAME,
  DOSIS_FONT_NAME,
  INTER_FONT_NAME,
  INTER_TIGHT_FONT_NAME,
  LATO_FONT_NAME,
  LIBRE_BASKERVILLE_FONT_NAME,
  DEPRECATED_LUSTRIA_FONT_NAME,
  MERRIWEATHER_FONT_NAME,
  MONTSERRAT_FONT_NAME,
  MULISH_FONT_NAME,
  NOTO_SANS_FONT_NAME,
  NUNITO_FONT_NAME,
  OPEN_SANS_FONT_NAME,
  POPPINS_FONT_NAME,
  PT_SERIF_FONT_NAME,
  RALEWAY_FONT_NAME,
  ROBOTO_FONT_NAME,
  ROKKITT_FONT_NAME,
  SIGNIKA_FONT_NAME,
  DEPRECATED_SOURCE_SANS_PRO_FONT_NAME,
  TINOS_FONT_NAME,
  WORK_SANS_FONT_NAME,
  SOURCE_SANS_3_FONT_NAME,
} from '@glass/web/modules/fonts/fontNames';

type FontLoader = () => Promise<NextFont>;

type FontSettings = {
  loader: FontLoader;
};

const handleDefaultLoader = (mod: { default: any }) => mod.default;

const ROBOTO_LOADER = () =>
  import('@glass/web/modules/fonts/templates/roboto').then(handleDefaultLoader);

const SOURCE_SANS_3_LOADER = () =>
  import('@glass/web/modules/fonts/templates/sourceSans3').then(handleDefaultLoader);

const resumeFonts: Record<ResumeFontName | DeprecatedResumeFontName, FontSettings> = {
  [ARIMO_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/arimo').then(handleDefaultLoader),
  },
  [CINZEL_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/cinzel').then(handleDefaultLoader),
  },
  [CRIMSON_PRO_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/crimsonPro').then(handleDefaultLoader),
  },
  [DOSIS_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/dosis').then(handleDefaultLoader),
  },
  [INTER_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/inter').then(handleDefaultLoader),
  },
  [INTER_TIGHT_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/interTight').then(handleDefaultLoader),
  },
  [LATO_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/lato').then(handleDefaultLoader),
  },
  [LIBRE_BASKERVILLE_FONT_NAME]: {
    loader: () =>
      import('@glass/web/modules/fonts/templates/libreBaskerville').then(handleDefaultLoader),
  },
  [MERRIWEATHER_FONT_NAME]: {
    loader: () =>
      import('@glass/web/modules/fonts/templates/merriweather').then(handleDefaultLoader),
  },
  [MONTSERRAT_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/montserrat').then(handleDefaultLoader),
  },
  [MULISH_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/mulish').then(handleDefaultLoader),
  },
  [NOTO_SANS_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/notoSans').then(handleDefaultLoader),
  },
  [NUNITO_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/nunito').then(handleDefaultLoader),
  },
  [OPEN_SANS_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/openSans').then(handleDefaultLoader),
  },
  [POPPINS_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/poppins').then(handleDefaultLoader),
  },
  [PT_SERIF_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/ptSerif').then(handleDefaultLoader),
  },
  [RALEWAY_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/raleway').then(handleDefaultLoader),
  },
  [ROBOTO_FONT_NAME]: {
    loader: ROBOTO_LOADER,
  },
  [ROKKITT_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/rokkitt').then(handleDefaultLoader),
  },
  [SIGNIKA_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/signika').then(handleDefaultLoader),
  },
  [SOURCE_SANS_3_FONT_NAME]: {
    loader: SOURCE_SANS_3_LOADER,
  },
  [TINOS_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/tinos').then(handleDefaultLoader),
  },
  [WORK_SANS_FONT_NAME]: {
    loader: () => import('@glass/web/modules/fonts/templates/workSans').then(handleDefaultLoader),
  },
  // DEPRECATED FONTS
  [DEPRECATED_SOURCE_SANS_PRO_FONT_NAME]: {
    loader: SOURCE_SANS_3_LOADER,
  },
  [DEPRECATED_LUSTRIA_FONT_NAME]: {
    loader: () =>
      import('@glass/web/modules/fonts/templates/deprecated_lustria').then(handleDefaultLoader),
  },
  // INVALID FONTS MEANING FALLBACK ROBOTO WAS LIKELY USED INSTEAD
  [DEPRECATED_CINSEL_FONT_NAME]: {
    loader: ROBOTO_LOADER,
  },
  [DEPRECATED_MULI_FONT_NAME]: {
    loader: ROBOTO_LOADER,
  },
};

export default resumeFonts;
